// ---------------------------------------------------------------------------
// useInputsStateV3 — hook UNICO de polling do cockpit V3 (Onda 2.0 mai/2026).
// ---------------------------------------------------------------------------
// Substitui o trio ``fetchInputsV3Snapshot`` (refresh manual em
// InputsV3.js) + ``useBlocksV3`` (BlocksPane) + ``useDrawerDetailsV3``
// (InputDrawerV3). Resolve o problema de fontes de verdade
// divergentes: antes, 3 pollings independentes com timings distintos
// expunham estado inconsistente para o usuario (badge "acao
// necessaria" + cards "shimmer" + drawer "iniciando" no mesmo
// instante).
//
// Caracteristicas:
//   - 1 timer + 1 endpoint (``GET /api/v3/inputs/state/``).
//   - Polling adaptativo: 4s (vivo) / 30s (parado).
//   - ETag/304: tick "parado" custa 1 round-trip + 6 aggregations no
//     backend, sem trabalho de DB pesado.
//   - ``drawerMeta`` opcional: quando o usuario abre um InputDrawer,
//     o polling passa a incluir os ``drawer_details`` no MESMO
//     payload — garantia de sincronia entre cockpit e drawer.
//   - ``revision``: contador incrementado a cada payload novo. Hooks
//     downstream (status optimista) usam para detectar quando "minha
//     mudanca otimista foi sobrescrita pelo backend".
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchInputsStateV3 } from "../../../services/inputsV3Service";

const POLL_FAST_MS = 4000;
const POLL_SLOW_MS = 30_000;

// Heuristica "vivo": qualquer input com ``xlinks_in_flight > 0``,
// qualquer input em estado de processamento, ou qualquer bloco em
// ``running``/``waiting``. Quando o mes esta 100% terminal, caimos
// para 30s e dependemos do ETag para nao gerar trabalho.
// Onda 3.1: ``"running"`` foi unificado em ``"processing"`` nos
// state literals de CSL/BLK. ``InputControlV3`` nunca usou
// ``"running"``. Mantemos a chave em ambos os sets como compat
// defensiva (snapshots cacheados durante a transicao).
const ALIVE_INPUT_PHASES = new Set([
  "uploaded",
  "processing",
  "queued",
  "running",
  "needs_reupload",
]);
const ALIVE_BLOCK_STATUSES = new Set([
  "processing",
  "running",
  "waiting",
  "stale",
]);

// Adapta a secao ``drawer_details`` do payload consolidado para o
// shape consumido pelo ``InputDrawerV3`` (que ate a Onda 1.x recebia
// esse mesmo objeto via ``useDrawerDetailsV3``). Snake_case do
// backend -> camelCase do hook. Inclui ``refresh`` (encaminhado para
// o ``refresh()`` do hook unico) para preservar a API que os panes
// chamam quando o usuario faz uma acao manual (resolver decisao,
// adicionar nota, etc.).
export function shapeDrawerOverrideFromState(stateData, refresh) {
  if (!stateData) return null;
  const dd = stateData.drawer_details;
  if (!dd) return null;
  return {
    loading: false,
    about: dd.about || null,
    control: dd.control || null,
    currentMonthUploads: dd.current_month_uploads || [],
    previousMonthUpload: dd.previous_month_upload || null,
    notes: dd.notes || [],
    answeredDecisions: dd.answered_decisions || [],
    decisions: dd.decisions || [],
    crossLinks: dd.cross_links || [],
    auditRecent: dd.audit_recent || [],
    runtimeInput: dd.runtime_input
      ? {
          ...dd.runtime_input,
          meta_name: dd.runtime_input.meta_name || dd.about?.meta_name || null,
        }
      : null,
    monthStatus: dd.month_status || null,
    error: null,
    refresh: refresh || (() => {}),
  };
}

function isStatePayloadAlive(payload) {
  if (!payload) return false;
  const inputs = payload?.runtime?.runtime?.inputs || {};
  for (const meta of Object.keys(inputs)) {
    const it = inputs[meta] || {};
    if ((it.xlinks_in_flight || 0) > 0) return true;
    if (ALIVE_INPUT_PHASES.has(it.phase)) return true;
    const cs = it.control_state;
    if (cs === "uploaded" || cs === "processing") return true;
  }
  const blocks = payload?.blocks?.blocks || [];
  for (const b of blocks) {
    if (ALIVE_BLOCK_STATUSES.has(b?.status)) return true;
  }
  const ms = payload?.runtime?.month_status;
  if (ms === "processing" || ms === "waiting") return true;
  return false;
}

export default function useInputsStateV3({
  year,
  month,
  drawerMeta = null,
  enabled = true,
} = {}) {
  const canFetch = !!(enabled && year && month);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);

  // Refs para evitar reagendar o polling a cada render do consumer.
  const yearRef = useRef(year);
  const monthRef = useRef(month);
  const drawerRef = useRef(drawerMeta);
  // ``prevDrawerForResetRef``: snapshot do drawer aplicado na ULTIMA
  // execucao do effect (1) — usado SO para detectar borda dentro do
  // proprio effect (1). Diferente de ``drawerRef``, que e' atualizado
  // pelo effect dedicado L141+ ANTES do effect (1) rodar (ordem de
  // declaracao = ordem de execucao em React), e portanto ja reflete
  // o valor novo quando o effect (1) le. Sem este ref auxiliar, a
  // comparacao "drawer mudou?" sempre dava falso e o reset de
  // ``drawer_details`` nao acontecia.
  const prevDrawerForResetRef = useRef(drawerMeta);
  const etagRef = useRef(null);
  const dataRef = useRef(null);
  const mountedRef = useRef(true);
  const inFlightAbortRef = useRef(null);
  // ``pausedRef``: handlers de acao (upload/replicate/reprocess)
  // pausam o polling enquanto o backend processa a request. Sem
  // isso, um fetch em voo (disparado ANTES do click) chega depois
  // do POST com estado pre-acao e sobrescreve a UI. Pause: aborta
  // in-flight + para polling. Resume: dispara refresh imediato e
  // retoma o ciclo. Os handlers usam o padrao
  // ``pause(); await POST; resume({ refreshNow: true })``.
  const pausedRef = useRef(false);
  // Versao do "ciclo de pause": incrementada a cada ``resume()``,
  // forca o effect de polling a reagendar (sai do estado de "nao
  // agenda enquanto pausado" para "agenda novo tick").
  const [pauseEpoch, setPauseEpoch] = useState(0);

  useEffect(() => {
    yearRef.current = year;
    monthRef.current = month;
  }, [year, month]);
  useEffect(() => {
    // Trocou o ``drawerMeta``: invalida o ETag local, porque ele e
    // por (impl, year, month, drawer) e o cache anterior nao serve
    // pro novo escopo. Sem isso, o backend devolveria 304 com base
    // no ETag de um drawer diferente.
    if (drawerRef.current !== drawerMeta) {
      etagRef.current = null;
    }
    drawerRef.current = drawerMeta;
  }, [drawerMeta]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (inFlightAbortRef.current) {
        try {
          inFlightAbortRef.current.abort();
        } catch (_) {
          // controller ja abortado/finalizado — ignorar
        }
      }
    };
  }, []);

  const applyPayload = useCallback((payload) => {
    if (!mountedRef.current) return;
    dataRef.current = payload;
    setData(payload);
    setRevision((r) => r + 1);
    setError(null);
  }, []);

  const doFetch = useCallback(
    ({ ignoreEtag = false, signal } = {}) => {
      return fetchInputsStateV3({
        year: yearRef.current,
        month: monthRef.current,
        drawerMeta: drawerRef.current,
        etag: ignoreEtag ? null : etagRef.current,
        signal,
      })
        .then(({ data: payload, etag, notModified }) => {
          if (!mountedRef.current) return null;
          if (etag) etagRef.current = etag;
          if (notModified || !payload) return null;
          const payloadPeriod = payload?.runtime?.period || payload?.period;
          if (
            payloadPeriod &&
            (payloadPeriod.year !== yearRef.current ||
              payloadPeriod.month !== monthRef.current)
          ) {
            return null;
          }
          applyPayload(payload);
          return payload;
        })
        .catch((err) => {
          if (!mountedRef.current) return null;
          // Aborts vindos de troca de mes/drawer nao devem virar erro
          // visivel — sao parte do fluxo normal.
          const isAbort =
            err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
          if (!isAbort) setError(err);
          return null;
        });
    },
    [applyPayload],
  );

  // (1) Primeira carga / mudanca de periodo / mudanca de drawer.
  useEffect(() => {
    if (!canFetch) {
      setData(null);
      dataRef.current = null;
      etagRef.current = null;
      setLoading(false);
      return undefined;
    }
    // Reset ETag a cada mudanca de chave — o cache anterior eh de
    // outro periodo/drawer.
    etagRef.current = null;
    // NAO zeramos ``data`` aqui: a UX preferida e' manter os dados
    // do mes anterior visiveis (com fade), atualizando apenas quando
    // o novo payload chegar. Esvaziar a tela na transicao foi
    // explicitamente rejeitado pelo usuario. A guarda anti "payload
    // do mes errado vencer" vive em InputsV3.js (compara
    // ``payload.period`` com ``selectedMonthData``).
    //
    // EXCECAO (mai/2026, auditoria CCU Testes): a sub-secao
    // ``drawer_details`` precisa ser zerada na mudanca de drawer —
    // ela e' especifica do meta_name solicitado. Sem isso, o drawer
    // reusava o payload do INPUT ANTERIOR (notes, decisions, control,
    // runtime_input, ...) por ~200ms ate o novo fetch chegar, e o
    // ``shapeDrawerOverrideFromState`` retornava dados de outro input
    // com confianca silenciosa. Mantemos o resto do payload
    // (``runtime``, ``blocks``, ...) intacto para nao apagar o grid
    // por baixo do drawer durante a transicao.
    const drawerChanged = prevDrawerForResetRef.current !== drawerMeta;
    if (dataRef.current && drawerChanged) {
      const stripped = { ...dataRef.current, drawer_details: null };
      dataRef.current = stripped;
      setData(stripped);
    }
    prevDrawerForResetRef.current = drawerMeta;
    setLoading(true);
    if (inFlightAbortRef.current) {
      try {
        inFlightAbortRef.current.abort();
      } catch (_) {
        // controller ja abortado/finalizado — ignorar
      }
    }
    const controller = new AbortController();
    inFlightAbortRef.current = controller;
    doFetch({ ignoreEtag: true, signal: controller.signal }).finally(() => {
      if (!mountedRef.current) return;
      setLoading(false);
    });
    return () => {
      try {
        controller.abort();
      } catch (_) {
        // cleanup do effect anterior — abort pode ter sido emitido ja
      }
    };
  }, [canFetch, year, month, drawerMeta, doFetch]);

  // (2) Polling adaptativo (4s vivo / 30s parado). Pula ticks
  // enquanto ``pausedRef`` esta ativo.
  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    let timer = null;

    const schedule = () => {
      if (cancelled) return;
      const alive = isStatePayloadAlive(dataRef.current);
      const interval = alive ? POLL_FAST_MS : POLL_SLOW_MS;
      timer = setTimeout(tick, interval);
    };

    const tick = () => {
      if (cancelled) return;
      if (pausedRef.current) {
        // Pausado por handler de acao — nao polla, mas mantem o
        // ciclo agendado para retomar imediatamente quando resumir.
        schedule();
        return;
      }
      doFetch().finally(() => {
        if (!cancelled) schedule();
      });
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [canFetch, year, month, drawerMeta, doFetch, pauseEpoch]);

  // ``refresh()``: forca refetch imediato ignorando ETag local. Usado
  // por callsites que sabem que algo mudou (post-upload, post-
  // reprocess, post-decision, etc.).
  const refresh = useCallback(() => {
    return doFetch({ ignoreEtag: true });
  }, [doFetch]);

  // ``pause()``: aborta fetch em voo + bloqueia novos pollings.
  // Usado por handlers de acao (upload/replicate/reprocess) ANTES
  // do POST ao backend. Garante que payloads pre-acao nao chegam
  // depois e sobrescrevem a UI com estado obsoleto.
  const pause = useCallback(() => {
    pausedRef.current = true;
    if (inFlightAbortRef.current) {
      try {
        inFlightAbortRef.current.abort();
      } catch (_) {
        // controller ja abortado/finalizado — ignorar
      }
      inFlightAbortRef.current = null;
    }
  }, []);

  // ``resume({ refreshNow })``: libera polling e (opcional) dispara
  // refetch imediato. Usado por handlers DEPOIS do POST responder
  // (sucesso ou erro). ``refreshNow=true`` e' o caminho normal —
  // garante que a UI reflete o resultado da acao sem esperar a
  // proxima cadencia.
  const resume = useCallback(
    ({ refreshNow = true } = {}) => {
      pausedRef.current = false;
      setPauseEpoch((e) => e + 1);
      if (refreshNow) {
        return doFetch({ ignoreEtag: true });
      }
      return Promise.resolve(null);
    },
    [doFetch],
  );

  return useMemo(
    () => ({
      data,
      loading,
      error,
      revision,
      refresh,
      pause,
      resume,
    }),
    [data, loading, error, revision, refresh, pause, resume],
  );
}
